import type { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { ADDRESS_FIELDS, type AddressFormInput } from '../schemas/address-schema';

const FULL_WIDTH: ReadonlySet<(typeof ADDRESS_FIELDS)[number]> = new Set(['line1', 'line2']);

/**
 * The postal address inputs of a form whose values are an `AddressFormInput`; shared by the
 * order address dialog and the draft order address dialog.
 */
export function AddressFields<T extends FieldValues & AddressFormInput>({
  control,
}: {
  control: Control<T>;
}) {
  const { t } = useTranslation('orders');
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {ADDRESS_FIELDS.map((name) => (
        <FormField
          key={name}
          control={control}
          name={name as Path<T>}
          render={({ field }) => (
            <FormItem className={FULL_WIDTH.has(name) ? 'sm:col-span-2' : undefined}>
              <FormLabel>{t(`editAddress.${name}`)}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  maxLength={name === 'countryCode' ? 2 : undefined}
                  className={name === 'countryCode' ? 'uppercase' : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </div>
  );
}
